import { Box, Stack, useTheme } from "@mui/material";
import { Typography } from "@foodpilot/foods";
import type { Control, FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { RhfFoodsSelect, RhfFoodsTextField } from "../_shell/RhfFoodsFields";
import type { CompositePackagingCandidate } from "./compositeScore";
import type { CompositeEditorFormData } from "./compositeSchema";

type CompositeCompositionRowProps = {
    appearance?: "legacy" | "product-v2";
    control: Control<CompositeEditorFormData>;
    index: number;
    candidates: CompositePackagingCandidate[];
    errors: FieldErrors<CompositeEditorFormData>;
    tChild: string;
    tAmount: string;
    score?: string;
};

export const CompositeCompositionRow = ({
    appearance = "legacy",
    control,
    index,
    candidates,
    errors,
    tChild,
    tAmount,
    score,
}: CompositeCompositionRowProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const requiredFieldMessage = t("form.required_field", "This field is required.");
    const amountError = errors.composition?.[index]?.amount?.message;
    const childError = errors.composition?.[index]?.childId?.message;

    return (
        <Stack direction={{ xs: "column", sm: "row" }} gap={2} alignItems={{ sm: "flex-end" }} flex={1}>
            <Box sx={{ width: { xs: "100%", sm: theme.spacing(16.5) } }}>
                <RhfFoodsTextField
                    control={control}
                    name={`composition.${index}.amount`}
                    label={tAmount}
                    required
                    type="number"
                    min={0}
                    step={1}
                    errorMessage={amountError === "form.required_field" ? requiredFieldMessage : amountError}
                />
            </Box>
            <Box flex={1} minWidth={0}>
                <RhfFoodsSelect<CompositeEditorFormData, `composition.${number}.childId`, CompositePackagingCandidate>
                    appearance={appearance}
                    control={control}
                    name={`composition.${index}.childId`}
                    options={candidates}
                    getId={(packaging) => packaging.id}
                    getName={(packaging) => packaging.name}
                    label={tChild}
                    required
                    placeholder={tChild}
                    errorMessage={childError === "form.required_field" ? requiredFieldMessage : childError}
                />
            </Box>
            {score && (
                <Typography
                    variant="body2"
                    sx={{
                        minWidth: theme.spacing(9),
                        fontWeight: theme.typography.fontWeightBold,
                        alignSelf: "center",
                    }}
                >
                    {score}
                </Typography>
            )}
        </Stack>
    );
};
